import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, vModelText as _vModelText, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = { class: "text-lg font-medium text-gray-100" }
const _hoisted_4 = { class: "text-sm text-gray-400 mt-1" }
const _hoisted_5 = { class: "space-y-4" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "flex items-center gap-3" }
const _hoisted_8 = { class: "text-sm font-medium text-gray-300" }
const _hoisted_9 = { class: "text-xs text-gray-500" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "p-4 space-y-4 border-t border-gray-700" }
const _hoisted_12 = { class: "grid grid-cols-1 gap-4" }
const _hoisted_13 = { class: "space-y-2" }
const _hoisted_14 = { class: "grid grid-cols-1 md:grid-cols-2 gap-3" }
const _hoisted_15 = ["onUpdate:modelValue", "onInput"]
const _hoisted_16 = { class: "flex items-center gap-2 px-3 py-2 bg-dark/50 border border-gray-700 rounded-lg text-gray-400 text-sm" }
const _hoisted_17 = {
  key: 0,
  class: "text-gray-600 italic text-xs"
}
const _hoisted_18 = { class: "space-y-2" }
const _hoisted_19 = ["onUpdate:modelValue", "onChange"]
const _hoisted_20 = ["value"]
const _hoisted_21 = { class: "space-y-2" }
const _hoisted_22 = ["onUpdate:modelValue"]
const _hoisted_23 = {
  key: 0,
  class: "border-t border-gray-700 pt-4 space-y-4"
}
const _hoisted_24 = { class: "flex items-center justify-between" }
const _hoisted_25 = { class: "flex items-center gap-3" }
const _hoisted_26 = { class: "flex items-center gap-2" }
const _hoisted_27 = ["onUpdate:modelValue"]
const _hoisted_28 = { class: "flex items-center gap-2" }
const _hoisted_29 = ["onUpdate:modelValue"]
const _hoisted_30 = { class: "grid grid-cols-1 gap-4" }
const _hoisted_31 = { class: "space-y-2" }
const _hoisted_32 = ["value", "onInput"]
const _hoisted_33 = ["value"]
const _hoisted_34 = { class: "space-y-2" }
const _hoisted_35 = ["value", "onInput"]
const _hoisted_36 = {
  key: 1,
  class: "p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
}
const _hoisted_37 = {
  key: 0,
  class: "text-center py-8 bg-dark-secondary rounded-lg border border-gray-700"
}
const _hoisted_38 = { class: "flex justify-end items-center gap-3" }
const _hoisted_39 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_40 = ["disabled"]
const _hoisted_41 = {
  key: 0,
  class: "inline-block animate-spin"
}
const _hoisted_42 = {
  key: 1,
  class: "inline-block"
}

import { ref, computed, watch } from 'vue'

export interface SchemaField {
  displayName: string
  key: string
  type: 'string' | 'number' | 'boolean' | 'array'
  description: string
  minItems?: number
  maxItems?: number
  items?: {
    type: string
    description: string
  }
  errors?: string[]
}

interface TypeOption {
  value: 'string' | 'number' | 'boolean' | 'array'
  label: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SchemaBuilder',
  props: {
    modelValue: {},
    isSaving: { type: Boolean },
    title: {},
    subtitle: {}
  },
  emits: ["save"],
  setup(__props: any, { emit: __emit }) {

const supportedTypes: TypeOption[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'True/False' },
  { value: 'array', label: 'List' }
]

const getTypeLabel = (type: string) => {
  return supportedTypes.find(t => t.value === type)?.label || type
}

const props = __props

const schema = ref<SchemaField[]>([])
const isValid = ref(true)
const expandedFields = ref<boolean[]>([])
const lastSaved = ref<number | null>(null)
const saveSuccess = ref(false)

// Initialize schema from props
watch(() => props.modelValue, (newValue) => {
  schema.value = [...(newValue || [])]
}, { immediate: true })

// Watch for changes in field properties that should trigger validation
watch(() => schema.value.map(field => ({
  key: field.key,
  type: field.type,
  description: field.description,
  items: field.type === 'array' ? {
    type: field.items?.type,
    description: field.items?.description
  } : undefined,
  minItems: field.minItems,
  maxItems: field.maxItems
})), () => {
  validateSchema()
}, { deep: true, immediate: true })

// Watch schema length to keep expandedFields in sync
watch(() => schema.value.length, (newLength: number) => {
  // Ensure expandedFields array matches schema length
  expandedFields.value = expandedFields.value.slice(0, newLength)
  while (expandedFields.value.length < newLength) {
    expandedFields.value.push(false)
  }
}, { immediate: true })

const addField = () => {
  const newField: SchemaField = {
    displayName: '',
    key: '',
    type: 'string',
    description: '',
    errors: []
  }
  
  // Initialize array properties if type is array
  if (newField.type === 'array') {
    newField.items = {
      type: 'string',
      description: ''
    }
    newField.minItems = 0
    newField.maxItems = 10
  }
  
  schema.value.push(newField)
  // New field will be automatically expanded by the watcher
  expandedFields.value.push(true)
  validateSchema()
}

const toggleField = (index: number) => {
  if (index >= 0 && index < expandedFields.value.length) {
    expandedFields.value[index] = !expandedFields.value[index]
  }
}

const updateFieldKey = (field: SchemaField) => {
  // Convert display name to snake_case
  field.key = field.displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '_') // Replace spaces with underscores
}

const removeField = (index: number) => {
  if (index >= 0 && index < schema.value.length) {
    schema.value.splice(index, 1)
    expandedFields.value.splice(index, 1)
    validateSchema()
  }
}

const handleTypeChange = (field: SchemaField) => {
  // Initialize or remove array-specific properties
  if (field.type === 'array') {
    // Ensure items object is properly initialized
    field.items = {
      type: 'string',
      description: ''
    }
    field.minItems = 0
    field.maxItems = 10
  } else {
    // Clean up array properties if type is changed from array
    field.items = undefined
    field.minItems = undefined
    field.maxItems = undefined
  }

  // Validation will be triggered by the watcher
}

function validateSchema() {
  const newErrors = new Map<number, string[]>()
  
  // Validate each field
  schema.value.forEach((field, index) => {
    const errors: string[] = []
    
    // Validate key
    if (!field.key) {
      errors.push('Field key is required')
    } else if (!/^[a-z][a-z0-9_]*$/.test(field.key)) {
      errors.push('Field key must be in snake_case and start with a letter')
    }

    // Validate description
    if (!field.description?.trim()) {
      errors.push('Description is required')
    }

    // Validate array specific fields
    if (field.type === 'array') {
      if (!field.items?.description?.trim()) {
        errors.push('Array item description is required')
      }
      if (field.minItems !== undefined && field.maxItems !== undefined && field.minItems > field.maxItems) {
        errors.push('Minimum items cannot be greater than maximum items')
      }
    }

    if (errors.length > 0) {
      newErrors.set(index, errors)
    }
  })

  // Check for duplicate keys
  const keys = schema.value.map(field => field.key)
  const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index)
  
  if (duplicates.length > 0) {
    schema.value.forEach((field, index) => {
      if (duplicates.includes(field.key)) {
        const errors = newErrors.get(index) || []
        errors.push('Duplicate field key')
        newErrors.set(index, errors)
      }
    })
  }

  // Update errors and validity in a single batch
  schema.value.forEach((field, index) => {
    field.errors = newErrors.get(index) || []
  })
  isValid.value = schema.value.length > 0 && newErrors.size === 0
}

const updateArrayItemType = (field: SchemaField, event: Event) => {
  const value = (event.target as HTMLSelectElement).value
  if (!field.items) {
    field.items = {
      type: value,
      description: ''
    }
  } else {
    field.items.type = value
  }
}

const updateArrayItemDescription = (field: SchemaField, event: Event) => {
  const value = (event.target as HTMLTextAreaElement).value
  if (!field.items) {
    field.items = {
      type: 'string',
      description: value
    }
  } else {
    field.items.description = value
  }
}

const saveSchema = () => {
  if (!isValid.value) return
  
  // Emit the schema to parent component
  emit('save', schema.value)
  lastSaved.value = Date.now()
  
  // Show success state temporarily
  saveSuccess.value = true
  setTimeout(() => {
    saveSuccess.value = false
  }, 2000)
}

const emit = __emit

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, [
        _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.title), 1),
        _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.subtitle), 1)
      ]),
      _createElementVNode("button", {
        onClick: addField,
        class: "px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 text-sm"
      }, _cache[1] || (_cache[1] = [
        _createElementVNode("svg", {
          xmlns: "http://www.w3.org/2000/svg",
          class: "h-4 w-4",
          viewBox: "0 0 20 20",
          fill: "currentColor"
        }, [
          _createElementVNode("path", {
            "fill-rule": "evenodd",
            d: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z",
            "clip-rule": "evenodd"
          })
        ], -1),
        _createTextVNode(" Add Field ")
      ]))
    ]),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(schema.value, (field, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: "bg-dark-secondary rounded-lg border border-gray-700 hover:border-primary/30 transition-all duration-300"
        }, [
          _createElementVNode("div", {
            class: "p-4 flex items-center justify-between cursor-pointer select-none",
            onClick: ($event: any) => (toggleField(index))
          }, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", {
                class: _normalizeClass(["transform transition-transform duration-200", expandedFields.value[index] ? 'rotate-90' : ''])
              }, _cache[2] || (_cache[2] = [
                _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  class: "h-4 w-4 text-gray-400",
                  viewBox: "0 0 20 20",
                  fill: "currentColor"
                }, [
                  _createElementVNode("path", {
                    "fill-rule": "evenodd",
                    d: "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z",
                    "clip-rule": "evenodd"
                  })
                ], -1)
              ]), 2),
              _createElementVNode("span", _hoisted_8, _toDisplayString(field.displayName || 'New Field'), 1),
              _createElementVNode("span", _hoisted_9, _toDisplayString(field.key || 'generated_key'), 1),
              _createElementVNode("span", {
                class: _normalizeClass(["px-2 py-0.5 text-xs rounded-full", {
                'bg-blue-500/10 text-blue-400': field.type === 'string',
                'bg-green-500/10 text-green-400': field.type === 'number',
                'bg-yellow-500/10 text-yellow-400': field.type === 'boolean',
                'bg-purple-500/10 text-purple-400': field.type === 'array'
              }])
              }, _toDisplayString(getTypeLabel(field.type)), 3)
            ]),
            _createElementVNode("div", {
              class: "flex items-center gap-3",
              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createElementVNode("button", {
                onClick: ($event: any) => (removeField(index)),
                class: "p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-dark"
              }, _cache[3] || (_cache[3] = [
                _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  class: "h-4 w-4",
                  viewBox: "0 0 20 20",
                  fill: "currentColor"
                }, [
                  _createElementVNode("path", {
                    "fill-rule": "evenodd",
                    d: "M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z",
                    "clip-rule": "evenodd"
                  })
                ], -1)
              ]), 8, _hoisted_10)
            ])
          ], 8, _hoisted_6),
          _withDirectives(_createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _cache[6] || (_cache[6] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-300" }, [
                  _createTextVNode(" Field Name & Key "),
                  _createElementVNode("span", { class: "text-red-500" }, "*")
                ], -1)),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("div", null, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": ($event: any) => ((field.displayName) = $event),
                      type: "text",
                      placeholder: "e.g., User Name",
                      class: "w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                      onInput: ($event: any) => (updateFieldKey(field))
                    }, null, 40, _hoisted_15), [
                      [_vModelText, field.displayName]
                    ]),
                    _cache[4] || (_cache[4] = _createElementVNode("p", { class: "mt-1.5 text-xs text-gray-500" }, " Enter a human-readable name ", -1))
                  ]),
                  _createElementVNode("div", null, [
                    _createElementVNode("div", _hoisted_16, [
                      _createElementVNode("span", null, _toDisplayString(field.key || 'generated_key'), 1),
                      (!field.key)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_17, "(auto-generated)"))
                        : _createCommentVNode("", true)
                    ]),
                    _cache[5] || (_cache[5] = _createElementVNode("p", { class: "mt-1.5 text-xs text-gray-500" }, " Auto-generated snake_case key ", -1))
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_18, [
                _cache[7] || (_cache[7] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-300" }, [
                  _createTextVNode(" Field Type "),
                  _createElementVNode("span", { class: "text-red-500" }, "*")
                ], -1)),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": ($event: any) => ((field.type) = $event),
                  class: "w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                  onChange: ($event: any) => (handleTypeChange(field))
                }, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(supportedTypes, (type) => {
                    return _createElementVNode("option", {
                      key: type.value,
                      value: type.value
                    }, _toDisplayString(type.label), 9, _hoisted_20)
                  }), 64))
                ], 40, _hoisted_19), [
                  [_vModelSelect, field.type]
                ]),
                _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-xs text-gray-500" }, " Choose how this field should store its data ", -1))
              ]),
              _createElementVNode("div", _hoisted_21, [
                _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-300" }, [
                  _createTextVNode(" Description "),
                  _createElementVNode("span", { class: "text-red-500" }, "*")
                ], -1)),
                _withDirectives(_createElementVNode("textarea", {
                  "onUpdate:modelValue": ($event: any) => ((field.description) = $event),
                  rows: "2",
                  placeholder: "Describe what this field is used for...",
                  class: "w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                }, null, 8, _hoisted_22), [
                  [_vModelText, field.description]
                ])
              ])
            ]),
            (field.type === 'array')
              ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                  _createElementVNode("div", _hoisted_24, [
                    _cache[12] || (_cache[12] = _createElementVNode("h4", { class: "text-sm font-medium text-gray-300" }, "Array Settings", -1)),
                    _createElementVNode("div", _hoisted_25, [
                      _createElementVNode("div", _hoisted_26, [
                        _cache[10] || (_cache[10] = _createElementVNode("span", { class: "text-xs text-gray-500" }, "Min", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((field.minItems) = $event),
                          type: "number",
                          min: "0",
                          placeholder: "0",
                          class: "w-20 px-2 py-1 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        }, null, 8, _hoisted_27), [
                          [
                            _vModelText,
                            field.minItems,
                            void 0,
                            { number: true }
                          ]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_28, [
                        _cache[11] || (_cache[11] = _createElementVNode("span", { class: "text-xs text-gray-500" }, "Max", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((field.maxItems) = $event),
                          type: "number",
                          min: "1",
                          placeholder: "10",
                          class: "w-20 px-2 py-1 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        }, null, 8, _hoisted_29), [
                          [
                            _vModelText,
                            field.maxItems,
                            void 0,
                            { number: true }
                          ]
                        ])
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_30, [
                    _createElementVNode("div", _hoisted_31, [
                      _cache[13] || (_cache[13] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-300" }, [
                        _createTextVNode(" Item Type "),
                        _createElementVNode("span", { class: "text-red-500" }, "*")
                      ], -1)),
                      _createElementVNode("select", {
                        value: field.items?.type || 'string',
                        onInput: ($event: any) => (updateArrayItemType(field, $event)),
                        class: "w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(supportedTypes.filter(t => t.value !== 'array'), (type) => {
                          return (_openBlock(), _createElementBlock("option", {
                            key: type.value,
                            value: type.value
                          }, _toDisplayString(type.label), 9, _hoisted_33))
                        }), 128))
                      ], 40, _hoisted_32)
                    ]),
                    _createElementVNode("div", _hoisted_34, [
                      _cache[14] || (_cache[14] = _createElementVNode("label", { class: "block text-sm font-medium text-gray-300" }, [
                        _createTextVNode(" Item Description "),
                        _createElementVNode("span", { class: "text-red-500" }, "*")
                      ], -1)),
                      _createElementVNode("textarea", {
                        value: field.items?.description || '',
                        onInput: ($event: any) => (updateArrayItemDescription(field, $event)),
                        rows: "2",
                        placeholder: "Describe what each item in the array represents...",
                        class: "w-full px-3 py-2 bg-dark border border-gray-700 rounded-lg text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      }, null, 40, _hoisted_35)
                    ])
                  ])
                ]))
              : _createCommentVNode("", true),
            (field.errors?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_36, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(field.errors, (error) => {
                    return (_openBlock(), _createElementBlock("p", {
                      key: error,
                      class: "text-sm text-red-400"
                    }, _toDisplayString(error), 1))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ], 512), [
            [_vShow, expandedFields.value[index]]
          ])
        ]))
      }), 128))
    ]),
    (schema.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_37, _cache[15] || (_cache[15] = [
          _createElementVNode("svg", {
            xmlns: "http://www.w3.org/2000/svg",
            class: "h-12 w-12 mx-auto text-gray-500",
            fill: "none",
            viewBox: "0 0 24 24",
            stroke: "currentColor"
          }, [
            _createElementVNode("path", {
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              "stroke-width": "2",
              d: "M12 6v6m0 0v6m0-6h6m-6 0H6"
            })
          ], -1),
          _createElementVNode("p", { class: "mt-2 text-gray-400" }, "No fields added yet. Click \"Add Field\" to start building your schema.", -1)
        ])))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_38, [
      (lastSaved.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_39, " Last saved: " + _toDisplayString(new Date(lastSaved.value).toLocaleString()), 1))
        : _createCommentVNode("", true),
      _createElementVNode("button", {
        onClick: saveSchema,
        disabled: !isValid.value || schema.value.length === 0,
        class: _normalizeClass(["px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2", [
          saveSuccess.value ? 'bg-green-500 hover:bg-green-500/90 text-white' : 'bg-primary hover:bg-primary/90 text-white'
        ]])
      }, [
        (props.isSaving)
          ? (_openBlock(), _createElementBlock("span", _hoisted_41, _cache[16] || (_cache[16] = [
              _createElementVNode("svg", {
                class: "w-4 h-4",
                xmlns: "http://www.w3.org/2000/svg",
                fill: "none",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("circle", {
                  class: "opacity-25",
                  cx: "12",
                  cy: "12",
                  r: "10",
                  stroke: "currentColor",
                  "stroke-width": "4"
                }),
                _createElementVNode("path", {
                  class: "opacity-75",
                  fill: "currentColor",
                  d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                })
              ], -1)
            ])))
          : (saveSuccess.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_42, _cache[17] || (_cache[17] = [
                _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  class: "h-4 w-4",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M5 13l4 4L19 7"
                  })
                ], -1)
              ])))
            : _createCommentVNode("", true),
        _createElementVNode("span", null, _toDisplayString(props.isSaving ? 'Saving...' : saveSuccess.value ? 'Saved!' : 'Save Schema'), 1)
      ], 10, _hoisted_40)
    ])
  ]))
}
}

})